import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Offcanvas, Button, Form } from 'react-bootstrap';
import { BsPencil, BsCheck, BsX } from 'react-icons/bs';
import {
    FaFileContract,
    FaCalendarAlt,
    FaHistory,
    FaUserLock,
    FaRocket,
    FaInfoCircle,
    FaIdCard,
    FaShieldAlt,
    FaUsers,
    FaKey,
    FaRegCalendarCheck,
    FaChevronDown,
    FaChevronUp,
    FaPaperclip
} from "react-icons/fa";
import LicenseModal from './LicenseModal';
import Swal from 'sweetalert2';

const LicenseDetails = ({ show, onHide, licenseId }) => {
    const [licenseDetails, setLicenseDetails]   = useState(null);
    const [editedLicenseDetails, setEditedLicenseDetails] = useState(null);
    const [editField, setEditField]             = useState(null);
    const [showModal, setShowModal]             = useState(false);
    const [activeTab, setActiveTab]             = useState('licenseDetails');
    const [extensionDetails, setExtensionDetails] = useState([]);
    const [localShow, setLocalShow]             = useState(show);
    const [licenseTypes, setLicenseTypes]       = useState([]);
    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    };

    useEffect(() => { setLocalShow(show); }, [show]);

    useEffect(() => {
        if (localShow && licenseId) {
            setLicenseDetails(null);
            setActiveTab('licenseDetails');
            fetchLicenseDetails();
            fetchLicenseTypes();
        }
    }, [localShow, licenseId]);

    const fetchLicenseTypes = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/license-types`, {
                headers: { 'Authorization': `Bearer ${token}` , 'Tenant-Origin': origin},
                skipLoader: true,
            });
            const dataObj = response.data.Data;
            setLicenseTypes(dataObj?.data || dataObj || []);
        } catch (error) {
            console.error('Error fetching license types:', error);
        }
    };

    const fetchLicenseDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo   = JSON.parse(localStorage.getItem('user-info'));
            const origin     = localStorage.getItem('user-origin');
            const token      = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/licenses/${licenseId}`, {
                headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
                skipLoader: true,
            });

            const data = response.data.Data || response.data.data;
            setLicenseDetails(data);
            setEditedLicenseDetails(data);
        } catch (error) {
            console.error('Error fetching license details:', error);
        }
    };

    const fetchExtensionDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo   = JSON.parse(localStorage.getItem('user-info'));
            const origin     = localStorage.getItem('user-origin');
            const token      = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/licenses/${licenseId}/extensions`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin },
                skipLoader: true,
            });
            setExtensionDetails(response.data.data || []);
        } catch (error) {
            console.error('Error fetching extension details:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const updatedValues = { ...editedLicenseDetails, [name]: value };
        setEditedLicenseDetails(updatedValues);
    };

    const handleSave = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo   = JSON.parse(localStorage.getItem('user-info'));
            const origin     = localStorage.getItem('user-origin');
            const token      = userInfo?.token;

            await axios.post(`${backendUrl}/api/licenses/${licenseId}?_method=PUT`, editedLicenseDetails, {
                headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
            });

            setLicenseDetails({ ...editedLicenseDetails });
            setEditField(null);

            Swal.fire({ toast: true, position: 'top-end', icon: 'success', title: 'Saved!', timer: 2000, showConfirmButton: false });
        } catch (error) {
            console.error('Error saving license details:', error);
        }
    };

    const editLicense = async (licenseId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo   = JSON.parse(localStorage.getItem('user-info'));
            const origin     = localStorage.getItem('user-origin');
            const token      = userInfo?.token;

            const updateUrl = `${backendUrl}/api/licenses/${licenseId}?_method=PUT`;

            let response;
            if (updatedData instanceof FormData) {
                // For FormData, let the browser handle the Content-Type with boundary
                response = await axios.post(updateUrl, updatedData, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
                });
            } else {
                response = await axios.post(updateUrl, updatedData, {
                    headers: { 'Authorization': `Bearer ${token}`, 'Tenant-Origin': origin },
                    params:  { tenant_info_id: licenseDetails?.tenant_info_id },
                });
            }

            const updatedLicense = response.data?.Data?.[0];
            if (updatedLicense) {
                setLicenseDetails(prev => ({ ...prev, ...updatedLicense }));
                setEditedLicenseDetails(prev => ({ ...prev, ...updatedLicense }));
            }
            setShowModal(false);
        } catch (error) {
            console.error('Error updating license:', error);
        }
    };

    const handleClose = () => {
        setLicenseDetails(null);
        setEditField(null);
        setActiveTab('licenseDetails');
        onHide();
    };

    // Build dynamic categories based on licenseTypes
    const modulesFields = [];
    const licenseFields = [];
    
    licenseTypes.forEach(type => {
        const isModule = type.type && (type.type.toLowerCase() === 'module' || type.type.toLowerCase() === 'model');
        if (isModule) {
            modulesFields.push(type.name);
        } else {
            licenseFields.push(type.name);
        }
    });

    const dynamicBreakdown = [];
    if (licenseFields.length > 0) {
        dynamicBreakdown.push({
            name: 'License Attributes',
            fields: [...new Set(licenseFields)],
            icon: <FaShieldAlt className="text-primary" />,
            color: '#6366f1'
        });
    }
    if (modulesFields.length > 0) {
        dynamicBreakdown.push({
            name: 'Modules',
            fields: [...new Set(modulesFields)],
            icon: <FaRocket className="text-warning" />,
            color: '#f59e0b'
        });
    }

    const getLicenseValue = (fieldName) => {
        if (!licenseDetails) return 0;
        const customData = licenseDetails.custom_fields && typeof licenseDetails.custom_fields === 'string'
            ? JSON.parse(licenseDetails.custom_fields)
            : (licenseDetails.custom_fields || {});
        return Number(licenseDetails[fieldName] || customData[fieldName] || 0);
    };

    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return 'N/A';
        const datePart = String(dateStr).split('T')[0];
        const parts = datePart.split('-');
        if (parts.length === 3) {
            return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
        return dateStr;
    };

    const staticFields = [
        { label: 'Max Login', name: 'max_login', value: licenseDetails?.max_login || '0', icon: FaKey },
        { label: 'Max Create', name: 'max_create', value: licenseDetails?.max_create || '0', icon: FaShieldAlt },
        { label: 'Start Date', name: 'start_date', value: formatDisplayDate(licenseDetails?.start_date), icon: FaCalendarAlt },
        { label: 'Validity Date', name: 'validity', value: formatDisplayDate(licenseDetails?.validity), icon: FaRegCalendarCheck },
        { label: 'Remarks', name: 'remarks', value: licenseDetails?.remarks || 'N/A', icon: FaInfoCircle, type: 'textarea' }
    ];

    const isActive = (license) => {
        if (!license) return false;
        const validityStr = license.validity || '';
        if (!validityStr) return true;
        const [y, m, d] = validityStr.split('-').map(Number);
        const expiryDate = new Date(y, m - 1, d);
        return expiryDate >= new Date().setHours(0,0,0,0);
    };

    return (
        <>
            <Offcanvas
                show={localShow && !showModal}
                onHide={handleClose}
                placement="end"
                style={{ width: '1150px', zIndex: 1055 }}
                className="License-offcanvas"
            >
                <Offcanvas.Header className="border-bottom bg-white py-3">
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <div className="d-flex align-items-center">
                            <div className="bg-primary rounded-circle p-2 me-3 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '42px', height: '42px' }}>
                                <FaFileContract className="text-white" size={20} />
                            </div>
                            <div>
                                <h5 className="mb-0 fw-bolder text-dark" style={{ letterSpacing: '-0.3px' }}>{licenseDetails?.tenant?.name || 'License Details'}</h5>
                                <div className="d-flex align-items-center gap-2 mt-1">
                                    <span className="badge bg-soft-primary text-primary font-size-10 fw-bold px-2">INSTANCE: {licenseDetails?.instance?.setup_name || 'N/A'}</span>
                                    <span className={`badge font-size-10 fw-bold px-2 ${isActive(licenseDetails) ? 'bg-soft-success text-success' : 'bg-soft-danger text-danger'}`}>
                                        {isActive(licenseDetails) ? 'ACTIVE' : 'EXPIRED'}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <Button
                                variant="primary"
                                size="sm"
                                className="rounded-pill px-3 shadow-sm border-0 d-flex align-items-center gap-2"
                                onClick={() => setShowModal(true)}
                                style={{ backgroundColor: '#6366f1' }}
                            >
                                <BsPencil size={14} /> <span className="fw-bold font-size-12">Edit</span>
                            </Button>
                            <Button variant="light" size="sm" onClick={handleClose} className="rounded-circle border-0 p-2">
                                <BsX size={22} className="text-muted" />
                            </Button>
                        </div>
                    </div>
                </Offcanvas.Header>

                <Offcanvas.Body className="p-0 bg-light bg-opacity-10">
                    <div className="border-bottom bg-white shadow-sm sticky-top" style={{ zIndex: 10 }}>
                        <ul className="nav nav-tabs nav-tabs-custom nav-justified border-0" role="tablist">
                            <li className="nav-item">
                                <button
                                    className={`nav-link border-0 py-3 fw-bolder font-size-12 text-uppercase ls-1 ${activeTab === 'licenseDetails' ? 'active' : 'text-muted'}`}
                                    onClick={() => setActiveTab('licenseDetails')}
                                >
                                    <FaInfoCircle className="me-2" size={14} />
                                    License Configuration
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link border-0 py-3 fw-bolder font-size-12 text-uppercase ls-1 ${activeTab === 'extensionInfo' ? 'active' : 'text-muted'}`}
                                    onClick={() => { setActiveTab('extensionInfo'); fetchExtensionDetails(); }}
                                >
                                    <FaHistory className="me-2" size={14} />
                                    Extension Archive
                                </button>
                            </li>
                            {licenseDetails?.po_attachment && (
                                <li className="nav-item">
                                    <button
                                        className={`nav-link border-0 py-3 fw-bolder font-size-12 text-uppercase ls-1 ${activeTab === 'poAttachment' ? 'active' : 'text-muted'}`}
                                        onClick={() => setActiveTab('poAttachment')}
                                    >
                                        <FaPaperclip className="me-2" size={14} />
                                        PO Attachment
                                    </button>
                                </li>
                            )}
                        </ul>
                    </div>

                    <div className="p-4" style={{ minHeight: '500px' }}>
                        {!licenseDetails ? (
                            <div className="text-center py-5">
                                <div className="spinner-grow text-primary" role="status" style={{ width: '3rem', height: '3rem' }} />
                                <p className="mt-3 text-muted fw-bold font-size-13 text-uppercase ls-1">Retrieving Records...</p>
                            </div>
                        ) : activeTab === 'licenseDetails' ? (
                            <>
                                {/* Quick Summary Overview Cards */}
                                <div className="row g-3 mb-4">
                                    {[
                                        { label: 'Validity Date', val: formatDisplayDate(licenseDetails?.validity), icon: FaRegCalendarCheck, color: '#6366f1', bg: 'rgba(99, 102, 241, 0.08)' },
                                        { label: 'Max Login', val: licenseDetails?.max_login, icon: FaUsers, color: '#10b981', bg: 'rgba(16, 185, 129, 0.08)' },
                                        { label: 'Max Create', val: licenseDetails?.max_create, icon: FaShieldAlt, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)' },
                                        { label: 'Status', val: isActive(licenseDetails) ? 'Active' : 'Expired', icon: FaShieldAlt, color: isActive(licenseDetails) ? '#10b981' : '#ef4444', bg: isActive(licenseDetails) ? 'rgba(16, 185, 129, 0.08)' : 'rgba(239, 68, 68, 0.08)' }
                                    ].map((kpi, i) => (
                                        <div className="col-md-3" key={i}>
                                            <div className="bg-white p-3 rounded-4 shadow-sm border border-faint h-100">
                                                <div className="d-flex align-items-center mb-1">
                                                    <div className="rounded-circle p-2 me-2 d-flex align-items-center justify-content-center" style={{ backgroundColor: kpi.bg, color: kpi.color, width: '32px', height: '32px' }}>
                                                        <kpi.icon size={14} />
                                                    </div>
                                                    <span className="text-muted text-uppercase fw-bold font-size-10 ls-1">{kpi.label}</span>
                                                </div>
                                                <h5 className="mb-0 fw-bolder text-dark px-1">{kpi.val || '0'}</h5>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mb-5">
                                    {dynamicBreakdown.map((cat, catIdx) => {
                                        const catFields = cat.fields.filter(f => getLicenseValue(f) > 0);
                                        if (catFields.length === 0) return null;
                                        
                                        const isExpanded = expandedGroups[cat.name] !== undefined ? expandedGroups[cat.name] : (catIdx === 0);
                                        const isModuleCat = cat.name.toLowerCase().includes('module');

                                        return (
                                            <div key={cat.name} className="card border-0 shadow-sm mb-4 rounded-4 overflow-hidden">
                                                <div 
                                                    className="card-header border-0 d-flex justify-content-between align-items-center p-3" 
                                                    onClick={() => toggleGroup(cat.name)}
                                                    style={{ cursor: 'pointer', backgroundColor: isModuleCat ? 'rgba(16, 185, 129, 0.04)' : 'rgba(99, 102, 241, 0.04)' }}
                                                >
                                                    <div className="d-flex align-items-center mb-0">
                                                        <div className="bg-white rounded p-2 me-3 shadow-sm d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                                                            {cat.icon}
                                                        </div>
                                                        <h6 className={`mb-0 fw-bolder text-uppercase ${isModuleCat ? 'text-success' : 'text-primary'}`} style={{ fontSize: '12px', letterSpacing: '1px' }}>{cat.name}</h6>
                                                    </div>
                                                    <div className="bg-white rounded-circle p-1 shadow-xs mt-1">
                                                        {isExpanded ? <FaChevronUp size={12} className={isModuleCat ? 'text-success' : 'text-primary'} /> : <FaChevronDown size={12} className={isModuleCat ? 'text-success' : 'text-primary'} />}
                                                    </div>
                                                </div>
                                                {isExpanded && (
                                                    <div className="card-body p-4 bg-white">
                                                        <div className="row g-4">
                                                            {catFields.map((field, idx) => (
                                                                <div key={idx} className="col-md-3 col-sm-6">
                                                                    <div 
                                                                        className={`p-3 rounded-4 h-100 transition-all hover-transform border border-opacity-10 ${isModuleCat ? 'bg-soft-success border-success' : 'bg-soft-primary border-primary'}`}
                                                                        style={{ position: 'relative', overflow: 'hidden' }}
                                                                    >
                                                                        <div className="position-absolute opacity-10" style={{ right: '-5px', bottom: '-5px' }}>
                                                                            {cat.icon}
                                                                        </div>
                                                                        <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2">
                                                                            {field.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                                        </label>
                                                                        <div className="d-flex align-items-center gap-2">
                                                                            <h5 className={`mb-0 fw-black ${isModuleCat ? 'text-dark' : 'text-primary'}`}>{getLicenseValue(field)}</h5>
                                                                          
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                    
                                    {/* Additional / Uncategorized Attributes */}
                                    {(() => {
                                        const groupedFields = dynamicBreakdown.flatMap(cat => cat.fields);
                                        const others = licenseTypes.filter(t => !groupedFields.includes(t.name) && getLicenseValue(t.name) > 0);

                                        if (others.length === 0) return null;

                                        return (
                                            <div className="mt-4 p-4 rounded-4 border border-dashed border-2 m-2 bg-light bg-opacity-10">
                                                <div className="d-flex align-items-center mb-4">
                                                    <div className="bg-white rounded-circle p-2 shadow-xs me-3">
                                                        <FaIdCard className="text-muted" size={14} />
                                                    </div>
                                                    <h6 className="mb-0 fw-bolder text-muted text-uppercase ls-2 font-size-10">System Baseline Attributes</h6>
                                                    <div className="ms-3 flex-grow-1 border-bottom opacity-10"></div>
                                                </div>
                                                <div className="row g-3">
                                                    {others.map((t, idx) => (
                                                        <div key={idx} className="col-md-3 col-sm-6">
                                                            <div className="bg-white p-3 rounded-4 shadow-sm border border-faint h-100 transition-all hover-shadow-md">
                                                                <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-75">
                                                                    {t.name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                                </label>
                                                                <h6 className="mb-0 fw-black text-dark font-size-15">{getLicenseValue(t.name)}</h6>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>

                                <div className="card border-0 shadow-sm rounded-4 mb-5">
                                    <div className="card-body p-4">
                                        <div className="d-flex align-items-center mb-4">
                                            <div className="bg-soft-primary rounded p-2 me-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(85, 110, 230, 0.08)', width: '36px', height: '36px' }}>
                                                <FaInfoCircle className="text-primary" size={16} />
                                            </div>
                                            <h6 className="mb-0 fw-bolder text-uppercase ls-1 font-size-12 text-dark">Configuration Details & Remarks</h6>
                                        </div>
                                        <div className="row g-4">
                                            {staticFields.map((field, index) => (
                                                <div key={index} className={field.type === 'textarea' ? "col-12" : "col-md-4"}>
                                                    <div className="p-3 bg-light bg-opacity-25 rounded-3 border h-100">
                                                        <label className="text-muted text-uppercase fw-bold font-size-9 d-block ls-1 mb-2 opacity-50">
                                                            <field.icon className="me-2" size={10} /> {field.label}
                                                        </label>
                                                        {field.type === 'file' ? (
                                                            field.value ? (
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <span className="text-dark fw-bold font-size-12 text-truncate" style={{ maxWidth: '120px' }}>{field.value.split('/').pop()}</span>
                                                                    <a 
                                                                        href={`${process.env.REACT_APP_BACKEND_URL}/storage/${field.value}`} 
                                                                        target="_blank" 
                                                                        rel="noopener noreferrer"
                                                                        className="btn btn-xs btn-soft-primary rounded-pill px-2 py-1 font-size-10"
                                                                    >
                                                                        Open <FaRocket size={8} className="ms-1" />
                                                                    </a>
                                                                </div>
                                                            ) : (
                                                                <h6 className="mb-0 fw-bold text-muted opacity-50 font-size-13 py-1 italic">No File</h6>
                                                            )
                                                        ) : (
                                                            <h6 className="mb-0 fw-bold text-dark font-size-13" style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>{field.value || 'N/A'}</h6>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </>
                        ) : activeTab === 'poAttachment' ? (
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden" style={{ height: 'calc(100vh - 120px)' }}>
                                <div className="card-header bg-white p-3 border-bottom d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-soft-success rounded p-2 me-3 d-flex align-items-center justify-content-center" style={{ backgroundColor: 'rgba(16, 185, 129, 0.08)', width: '32px', height: '32px' }}>
                                            <FaPaperclip className="text-success" size={14} />
                                        </div>
                                        <div>
                                            <h6 className="mb-0 fw-bolder text-uppercase ls-1 font-size-11 text-dark">Document Preview</h6>
                                            <small className="text-muted font-size-9">{licenseDetails.po_attachment.split('/').pop()}</small>
                                        </div>
                                    </div>
                                    <a 
                                        href={`${process.env.REACT_APP_BACKEND_URL}/storage/${licenseDetails.po_attachment}`} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="btn btn-xs btn-outline-success rounded-pill px-3 py-1 font-size-10"
                                    >
                                        Full Screen View
                                    </a>
                                </div>
                                <div className="card-body p-0 flex-grow-1" style={{ backgroundColor: '#ffffff !important' }}>
                                    {licenseDetails.po_attachment.toLowerCase().endsWith('.pdf') ? (
                                        <iframe 
                                            src={`${process.env.REACT_APP_BACKEND_URL}/storage/${licenseDetails.po_attachment}#toolbar=0&navpanes=0`}
                                            width="100%" 
                                            height="100%" 
                                            style={{ border: 'none', backgroundColor: '#ffffff !important' }}
                                            className="bg-white"
                                            title="PO Attachment"
                                        />
                                    ) : (/\.(jpg|jpeg|png|webp|gif|svg)$/i).test(licenseDetails.po_attachment) ? (
                                        <div className="h-100 d-flex align-items-center justify-content-center p-4 bg-white">
                                            <img 
                                                src={`${process.env.REACT_APP_BACKEND_URL}/storage/${licenseDetails.po_attachment}`} 
                                                alt="PO" 
                                                className="img-fluid shadow-sm rounded-3" 
                                                style={{ maxHeight: '100%', objectFit: 'contain' }} 
                                            />
                                        </div>
                                    ) : (
                                        <div className="h-100 d-flex flex-column align-items-center justify-content-center p-5 bg-white text-center">
                                            <div className="bg-soft-primary rounded-circle p-4 mb-4" style={{ backgroundColor: 'rgba(99, 102, 241, 0.08)' }}>
                                                <i className="mdi mdi-file-word font-size-48 text-primary"></i>
                                            </div>
                                            <h5 className="fw-bolder">No Inline Preview Available</h5>
                                            <p className="text-muted mb-4 opacity-75">This file type ({licenseDetails.po_attachment.split('.').pop().toUpperCase()}) cannot be previewed in the browser directly.</p>
                                            <a 
                                                href={`${process.env.REACT_APP_BACKEND_URL}/storage/${licenseDetails.po_attachment}`} 
                                                download 
                                                className="btn btn-primary rounded-pill px-4 fw-bold"
                                            >
                                                Download Instead
                                            </a>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                                <div className="p-3 bg-soft-primary bg-opacity-10 border-bottom">
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light bg-opacity-50">
                                            <tr>
                                                <th className="ps-4 font-size-11 text-uppercase text-muted fw-bold py-3">#</th>
                                                <th className="font-size-11 text-uppercase text-muted fw-bold py-3">Execution Date</th>
                                                <th className="font-size-11 text-uppercase text-muted fw-bold py-3">Extended By</th>
                                                <th className="text-center font-size-11 text-uppercase text-muted fw-bold py-3">Archived Validity</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {extensionDetails.length === 0 ? (
                                                <tr><td colSpan={4} className="text-center text-muted py-5 font-size-13 italic">No extension history available for this record.</td></tr>
                                            ) : extensionDetails.map((ext, i) => (
                                                <tr key={ext.id}>
                                                    <td className="ps-4 text-muted font-size-12 fw-bold">{i + 1}</td>
                                                    <td>
                                                        <div className="text-dark fw-bold font-size-13">{ext.created_at ? new Date(ext.created_at).toLocaleDateString() : 'N/A'}</div>
                                                        <small className="text-muted font-size-10">{ext.created_at ? new Date(ext.created_at).toLocaleTimeString() : ''}</small>
                                                    </td>
                                                    <td>
                                                        <span className="badge rounded-pill bg-soft-success text-success px-3 py-2 font-size-11 fw-bold">
                                                            +{ext.extension_days} {ext.extension_type}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="text-primary fw-black font-size-14 text-nowrap">{formatDisplayDate(ext.new_validity_date)}</div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-light bg-opacity-20">
                                            <tr>
                                                <td colSpan={4} className="text-center py-2">
                                                    <small className="text-muted font-size-10 text-uppercase fw-bold ls-1">Audit Trail Protocol Secured</small>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </Offcanvas.Body>
            </Offcanvas>

            {showModal && licenseDetails && (
                <LicenseModal
                    showModal={showModal}
                    handleClose={() => setShowModal(false)}
                    selectedLicenseData={licenseDetails}
                    editLicense={editLicense}
                    tenantId={licenseDetails.tenant_info_id}
                    selectedLicenseId={licenseId}
                />
            )}
        </>
    );
};

export default LicenseDetails;
